import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutDashboard, Store, ClipboardList, ListOrdered, ArrowLeft, LogOut, Send, ShieldCheck, Settings, Percent, Menu, X, CheckCircle2, Users, Gift, HandHeart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { vendorAdminApi } from '../services/api';
import StoreSwitcher from './StoreSwitcher';

const NAV = [
  { to: '/vendor-admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/vendor-admin/store', icon: Store, label: 'Store' },
  { to: '/vendor-admin/menu', icon: ListOrdered, label: 'Menu' },
  { to: '/vendor-admin/orders', icon: ClipboardList, label: 'Orders' },
  { to: '/vendor-admin/completed-orders', icon: CheckCircle2, label: 'Completed orders' },
  { to: '/vendor-admin/team', icon: Users, label: 'Team' },
  { to: '/vendor-admin/promotions', icon: Percent, label: 'Promotions' },
  { to: '/vendor-admin/gift-pool', icon: Gift, label: 'Gift pool' },
  { to: '/vendor-admin/claim-store', icon: HandHeart, label: 'Claim a store' },
  { to: '/vendor-admin/settings', icon: Settings, label: 'Settings' },
];

export const useVendorContext = () => {
  const [params] = useSearchParams();
  const { user } = useAuth();
  // Super admin can impersonate via ?vendor_id=<id>; everyone else uses their own.
  const impersonating = !!params.get('vendor_id');
  const vendor_id = impersonating ? params.get('vendor_id') : (user?.vendor_id || null);
  return { vendor_id, impersonating };
};

const VendorAdminLayout = () => {
  const { user, logout, loading } = useAuth();
  const [params] = useSearchParams();
  const queryVendorId = params.get('vendor_id');
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  // Mobile-only slide-out drawer state (desktop keeps the fixed sidebar).
  // Must be declared with the other hooks so hook order stays stable across
  // the various early returns below.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Compute permission states up front to keep hook order stable.
  const hasVendor = !!user?.vendor_id;
  const isSuper = !!user?.is_admin;
  const allowed = !!user && (hasVendor || (isSuper && !!queryVendorId));

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    vendorAdminApi.store(queryVendorId || undefined)
      .then((s) => { if (alive) setStore(s); })
      .catch(() => { if (alive) setStore(null); });
    return () => { alive = false; };
  }, [allowed, queryVendorId, user?.vendor_id]);

  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 max-w-md text-center">
          <div className="h-12 w-12 rounded-full bg-amber-50 text-amber-600 mx-auto flex items-center justify-center font-bold">!</div>
          <h1 className="mt-4 text-xl font-semibold text-slate-900">No store linked</h1>
          <p className="text-slate-500 mt-1 text-sm">Your account isn’t linked to a vendor. Ask a platform admin to assign you a store.</p>
          <button onClick={() => nav('/')} className="mt-5 px-4 h-10 bg-slate-900 text-white rounded-lg hover:bg-slate-800">Back to app</button>
        </div>
      </div>
    );
  }

  const initials = (user.name || 'V').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const impersonating = isSuper && !!queryVendorId;

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      {/* Mobile top bar (visible only on <md) */}
      <div className="md:hidden sticky top-0 z-30 bg-emerald-950 text-white flex items-center gap-2 px-3 py-2 shadow-sm" data-testid="vendor-mobile-topbar">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="h-9 w-9 rounded-lg bg-emerald-800/50 hover:bg-emerald-800 flex items-center justify-center"
          aria-label="Open menu"
          data-testid="vendor-mobile-menu-open"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {store?.logo ? (
            <img src={store.logo} alt="" className="h-7 w-7 rounded-full object-cover ring-1 ring-emerald-500" />
          ) : (
            <div className="h-7 w-7 rounded-full bg-emerald-600 flex items-center justify-center">
              <Send className="h-3.5 w-3.5 text-white -rotate-12" strokeWidth={2.6} />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate leading-tight">{store?.name || 'My store'}</div>
            <div className="text-[9px] uppercase tracking-widest text-emerald-300 leading-none">Vendor portal</div>
          </div>
        </div>
      </div>

      {/* Mobile scrim */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileNavOpen(false)}
          data-testid="vendor-mobile-scrim"
        />
      )}

      <aside
        className={`w-64 bg-emerald-950 text-emerald-100 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 md:translate-x-0 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-testid="vendor-sidebar"
      >
        <button
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden absolute top-3 right-3 h-8 w-8 rounded-full bg-emerald-800/50 hover:bg-emerald-800 flex items-center justify-center"
          aria-label="Close menu"
          data-testid="vendor-mobile-menu-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-5 py-5 border-b border-emerald-900">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-emerald-600 flex items-center justify-center">
              <Send className="h-4 w-4 text-white -rotate-12" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-semibold text-white text-sm leading-tight">{store?.name || 'My store'}</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">Vendor portal</div>
            </div>
          </div>
          {impersonating && (
            <div className="mt-3 px-2 py-1.5 rounded-md bg-amber-500/15 text-amber-300 text-[11px] flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Impersonating as super admin
            </div>
          )}
        </div>

        {/* Multi-store switcher (only appears if the caller owns ≥ 1 store or wants to add one) */}
        {!isSuper && (
          <StoreSwitcher
            activeVendorId={user?.vendor_id || queryVendorId}
            activeStoreName={store?.name}
            activeLogo={store?.logo}
          />
        )}

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to + (queryVendorId ? `?vendor_id=${encodeURIComponent(queryVendorId)}` : '')}
              end={n.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-emerald-600 text-white' : 'text-emerald-200 hover:bg-emerald-900 hover:text-white'}`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-emerald-900">
          <button onClick={() => nav(isSuper ? '/admin/vendors' : '/')} className="w-full flex items-center gap-3 px-3 h-10 rounded-lg text-sm text-emerald-200 hover:bg-emerald-900 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> {isSuper ? 'Back to admin' : 'Back to app'}
          </button>
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-white shadow-inner" data-testid="vendor-sidebar-userbox">
            {store?.logo ? (
              <img src={store.logo} alt={store?.name || 'store'} className="h-9 w-9 rounded-full object-cover ring-2 ring-emerald-600" data-testid="vendor-sidebar-logo" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-emerald-700 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
            )}
            <div className="flex-1 min-w-0">
              <div
                className="text-[13px] font-extrabold tracking-[0.05em] uppercase truncate leading-tight"
                style={{ color: '#065F46' /* deep emerald green */ }}
                data-testid="vendor-sidebar-store-name"
                title={store?.name || user.name}
              >
                {store?.name || user.name}
              </div>
              <div className="text-[11px] text-slate-500 truncate" data-testid="vendor-sidebar-email">{user.email}</div>
            </div>
            <button onClick={() => { logout(); nav('/login'); }} className="text-slate-400 hover:text-rose-500" aria-label="Sign out" data-testid="vendor-sidebar-logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 md:ml-64">
        <main className="p-4 sm:p-6 md:p-8">
          <ApprovalBanner store={store} />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const ApprovalBanner = ({ store }) => {
  if (!store) return null;
  const status = store.approval_status;
  if (!status || status === 'approved') return null;
  if (status === 'pending') {
    return (
      <div className="mb-5 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3" data-testid="approval-banner-pending">
        <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-amber-900">Awaiting super-admin approval</div>
          <p className="text-xs text-amber-800/80 mt-0.5">
            Your store is set up — but sales are paused until a ShipitNow admin reviews it.
            Use this time to polish your menu, prices, photos, currency, and Stripe payouts.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="mb-5 rounded-2xl bg-rose-50 border border-rose-200 p-4 flex gap-3" data-testid="approval-banner-rejected">
        <ShieldCheck className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-rose-900">Store rejected</div>
          <p className="text-xs text-rose-800/80 mt-0.5">
            A ShipitNow admin reviewed your store and could not approve it yet. Reason:
            <span className="block mt-1 italic font-mono">{store.approval_reason || 'No reason provided.'}</span>
            Update the issues above, then ping support to request re-review.
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export default VendorAdminLayout;
